import React, { useState } from 'react';
import { Headset, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ASSIGNER_ROLES = ['admin', 'cs', 'cro', 'cco', 'super_admin'];

/**
 * Sales→CS handover nudge. When an estimate is Won/Approved but has no CS Lead
 * assigned, show a dismissible banner prompting an authorized user to assign
 * one (opens the Edit Conditions dialog, which hosts the CS Lead selector).
 * Dismissal is session-scoped per estimate so it doesn't nag repeatedly.
 */
export const CSLeadHandoverNudge = ({ estimate, onAssignClick }) => {
  const { user } = useAuth();
  const estimateId = estimate?.id;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(`cs-handover-nudge-dismissed-${estimateId}`) === '1';
    } catch {
      return false;
    }
  });

  const ws = estimate?.workflow_status || '';
  const isWon = ws === 'won' || estimate?.is_winner === true;
  const isApproved = ws === 'approved';
  const stageLabel = isWon ? 'Won' : isApproved ? 'Approved' : null;

  const hasCsLead = !!(estimate?.cs_lead && estimate.cs_lead.user_id);
  const canAssign = ASSIGNER_ROLES.includes(user?.role);

  if (!stageLabel || hasCsLead || !canAssign || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(`cs-handover-nudge-dismissed-${estimateId}`, '1');
    } catch {
      /* session storage unavailable — banner just stays dismissed in-memory */
    }
  };

  return (
    <div
      className="w-full px-4 sm:px-6 lg:px-8 py-2.5 bg-emerald-50 border-b border-emerald-200"
      data-testid="cs-handover-nudge"
    >
      <div className="flex items-center gap-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
          <Headset className="w-4 h-4 text-emerald-700" />
        </span>
        <p className="text-sm text-emerald-900 flex-1 min-w-0">
          <span className="font-semibold">{stageLabel}</span> — this deal has no{' '}
          <span className="font-semibold">CS Lead</span> yet. Assign one to kick off the
          Sales&nbsp;→&nbsp;CS handover.
        </p>
        <button
          type="button"
          onClick={onAssignClick}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
          data-testid="cs-handover-nudge-assign-btn"
        >
          <Headset className="w-3.5 h-3.5" />
          Assign CS Lead
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 p-1 rounded-md text-emerald-500 hover:text-emerald-800 hover:bg-emerald-100 transition-colors"
          data-testid="cs-handover-nudge-dismiss-btn"
          title="Dismiss for this session"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CSLeadHandoverNudge;
